import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useContent } from "@/lib/content";
import { useLang } from "@/lib/lang";
import { LOCALES, DEFAULT_LOCALE, detectLocaleFromPath } from "@/lib/locales";

/**
 * Comprehensive SEO component: HTML title, meta description, keywords, robots,
 * canonical, Open Graph, Twitter Card, JSON-LD structured data, hreflang alternates.
 *
 * Every page-level SEO override merges over `content.seo` globals from the CMS.
 *
 * @param {object} props
 * @param {string} [props.title] - page-specific title (will be templated by global seo.title_template)
 * @param {string} [props.description] - meta description override
 * @param {string} [props.image] - OG image URL (falls back to seo.default_og_image)
 * @param {string} [props.type="website"] - og:type
 * @param {string} [props.canonical] - canonical override (auto-computed from window.location if omitted)
 * @param {string[]} [props.keywords] - focus + secondary keywords for <meta name="keywords">
 * @param {string} [props.focusKeyword] - main keyword
 * @param {object} [props.article] - if og:type=article: {published, modified, author, section, tags}
 * @param {Array<{name, url}>} [props.breadcrumbs] - for JSON-LD BreadcrumbList
 * @param {object} [props.jsonLd] - extra raw JSON-LD object to inject
 * @param {boolean} [props.noindex] - opt-out of indexing
 */
export default function SEO({
  title,
  description,
  image,
  type = "website",
  canonical,
  keywords,
  focusKeyword,
  article,
  breadcrumbs,
  jsonLd,
  noindex,
}) {
  const { seo = {}, site = {}, branding = {} } = useContent();
  const { lang, locale: ctxLocale } = useLang();
  const { pathname } = useLocation();
  // Prefer the URL-derived locale (regional prefix) — falls back to the context locale.
  const pathLocale = detectLocaleFromPath(pathname) || ctxLocale || DEFAULT_LOCALE;
  // Path WITHOUT the /pt-br|/pt-pt|/es-py prefix (used for cross-locale alternate URLs).
  const strippedPath = (() => {
    const stripped = pathname.replace(new RegExp(`^/(${LOCALES.map((l) => l.prefix).join("|")})(?=/|$)`, "i"), "");
    return stripped || "/";
  })();

  const g = seo; // global SEO
  // User request: ALL pages must use the same <title> as the home page. When
  // `home_title_all_pages` is enabled (default true), we ignore per-page titles
  // and render `home_title`. If disabled, fall back to the previous template
  // behaviour for finer per-page control.
  // Per-route unique titles are the SEO best practice — Google wants each URL
  // to carry a distinct, descriptive title. Set `home_title_all_pages: true`
  // in the CMS SEO editor only if you deliberately want the same brand title
  // on every page (rare, and hurts organic ranking).
  const homeTitleAll = g.home_title_all_pages === true;
  const homeTitle = g.home_title || g.default_title || "Places | Agência de Branding e Marketing Imobiliário desde 1991";
  const template = g.title_template || "%s | Places";
  const perPageTitle = title
    ? (template.includes("%s") ? template.replace("%s", title) : `${title} | ${g.site_name || "Places"}`)
    : homeTitle;
  const finalTitle = homeTitleAll ? homeTitle : perPageTitle;
  const finalDesc = description || g.default_description || "";
  // Priority chain for the social preview image: explicit prop → CMS default
  // (SEO settings) → branding logo → SPA hard fallback. The last option
  // guarantees WhatsApp / LinkedIn / Facebook always get *some* preview
  // image so links don't render as bare text.
  const FALLBACK_OG = "https://agenciaplaces.com.br/og-default.jpg";
  const rawImage = image || g.default_og_image || branding?.logo_url || FALLBACK_OG;
  // siteUrl priority: env PUBLIC_SITE_URL → CMS seo.site_url → current origin
  const envSiteUrl = (process.env.REACT_APP_PUBLIC_SITE_URL || "").replace(/\/$/, "");
  const cmsSiteUrl = (g.site_url || "").replace(/\/$/, "");
  const originUrl = typeof window !== "undefined" ? window.location.origin : "";
  const siteUrl = envSiteUrl || cmsSiteUrl || originUrl;
  // og:image MUST be an absolute URL — WhatsApp / LinkedIn / Facebook reject
  // relative paths silently and render the card without an image. If the CMS
  // stored `/api/media/xxx`, we prefix it with the canonical site URL.
  const finalImage = /^https?:\/\//i.test(rawImage) ? rawImage : `${siteUrl || "https://agenciaplaces.com.br"}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`;
  // Canonical: build from siteUrl + REGIONAL path (avoids leaking preview host + strips UTMs)
  const canonUrl = canonical || `${siteUrl}${pathname}`.split("#")[0].split("?")[0];
  // Use BCP-47 for OG locale + HTML lang.
  const locale = pathLocale.ogLocale;
  const htmlLang = pathLocale.htmlLang;
  const twitter = g.twitter_handle || "";
  const kw = [
    focusKeyword,
    ...(Array.isArray(keywords) ? keywords : []),
    ...(Array.isArray(g.default_keywords) ? g.default_keywords : []),
  ].filter(Boolean).map((k) => String(k).trim()).filter(Boolean);
  const uniqueKw = [...new Set(kw)].slice(0, 15);

  // JSON-LD: WebSite (site-wide, only when we're on home)
  const websiteLd = g.enable_jsonld_website !== false ? {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: g.site_name || "Places",
    url: siteUrl,
    inLanguage: locale.replace("_", "-"),
    potentialAction: siteUrl ? { "@type": "SearchAction", target: `${siteUrl}/insights?kw={search_term_string}`, "query-input": "required name=search_term_string" } : undefined,
  } : null;

  // JSON-LD: Organization
  const orgLd = g.enable_jsonld_org !== false ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: g.organization_name || "Places",
    url: siteUrl || undefined,
    logo: g.organization_logo || branding?.logo_url || undefined,
    sameAs: (g.social_urls || []).filter(Boolean),
    contactPoint: (site.whatsapp || site.email) ? {
      "@type": "ContactPoint", contactType: "customer service",
      telephone: site.whatsapp ? `+${site.whatsapp}` : undefined,
      email: site.email || undefined,
      areaServed: "BR", availableLanguage: ["pt", "en", "es"],
    } : undefined,
  } : null;

  // JSON-LD: Article
  const articleLd = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title || finalTitle,
    description: finalDesc,
    image: finalImage ? [finalImage] : undefined,
    datePublished: article.published,
    dateModified: article.modified || article.published,
    author: article.author ? { "@type": "Person", name: article.author, url: article.authorUrl } : undefined,
    publisher: {
      "@type": "Organization",
      name: g.organization_name || "Places",
      logo: g.organization_logo ? { "@type": "ImageObject", url: g.organization_logo } : undefined,
    },
    articleSection: article.section,
    keywords: uniqueKw.join(", ") || undefined,
    mainEntityOfPage: canonUrl,
    inLanguage: locale.replace("_", "-"),
  } : null;

  // JSON-LD: Breadcrumbs
  const breadcrumbsLd = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.url,
    })),
  } : null;

  // JSON-LD: LocalBusiness — richer than Organization for a physical HQ.
  const localBusinessLd = g.enable_jsonld_localbusiness !== false ? {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${siteUrl}#business`,
    name: g.organization_name || "Places",
    image: g.organization_logo || branding?.logo_url || finalImage || undefined,
    url: siteUrl || undefined,
    telephone: site.whatsapp ? `+${site.whatsapp}` : undefined,
    email: site.email || undefined,
    description: finalDesc,
    priceRange: g.price_range || "$$$",
    address: site.address ? {
      "@type": "PostalAddress",
      streetAddress: site.address.street || site.address,
      addressLocality: site.address.city || "Goiânia",
      addressRegion: site.address.state || "GO",
      postalCode: site.address.zip,
      addressCountry: site.address.country || "BR",
    } : undefined,
    areaServed: [
      { "@type": "Country", name: "Brazil" },
      { "@type": "Country", name: "Portugal" },
      { "@type": "Country", name: "Paraguay" },
    ],
    sameAs: (g.social_urls || []).filter(Boolean),
    knowsAbout: ["Real Estate Marketing", "Real Estate Branding", "Marketing Imobiliário", "Branding Imobiliário", "Campanhas Imobiliárias"],
  } : null;

  // JSON-LD: WebPage — attaches breadcrumbs + primary entity to any page.
  const webPageLd = g.enable_jsonld_webpage !== false ? {
    "@context": "https://schema.org",
    "@type": article ? "Article" : "WebPage",
    "@id": canonUrl ? `${canonUrl}#webpage` : undefined,
    url: canonUrl,
    name: finalTitle,
    description: finalDesc,
    isPartOf: siteUrl ? { "@id": `${siteUrl}#website` } : undefined,
    inLanguage: locale.replace("_", "-"),
    primaryImageOfPage: finalImage ? { "@type": "ImageObject", url: finalImage } : undefined,
    about: localBusinessLd ? { "@id": `${siteUrl}#business` } : undefined,
  } : null;

  const lds = [websiteLd, orgLd, localBusinessLd, webPageLd, articleLd, breadcrumbsLd, jsonLd].filter(Boolean);
  const robots = noindex ? "noindex, nofollow" : (g.default_robots || "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

  return (
    <Helmet prioritizeSeoTags>
      <html lang={htmlLang} />
      <title>{finalTitle}</title>
      {finalDesc && <meta name="description" content={finalDesc} />}
      {uniqueKw.length > 0 && <meta name="keywords" content={uniqueKw.join(", ")} />}
      <meta name="robots" content={robots} />
      <meta name="author" content={g.organization_name || "Places"} />
      {g.publisher && <meta name="publisher" content={g.publisher} />}
      {focusKeyword && <meta name="parsely-tags" content={focusKeyword} />}

      {/* Canonical + hreflang — regional, no query params, no preview host */}
      {canonUrl && <link rel="canonical" href={canonUrl} />}
      {siteUrl && LOCALES.map((l) => (
        <link key={`hl-${l.code}`} rel="alternate" hrefLang={l.code} href={`${siteUrl}/${l.prefix}${strippedPath === "/" ? "" : strippedPath}`} />
      ))}
      {siteUrl && (
        <link rel="alternate" hrefLang="x-default" href={`${siteUrl}/${DEFAULT_LOCALE.prefix}${strippedPath === "/" ? "" : strippedPath}`} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      {finalDesc && <meta property="og:description" content={finalDesc} />}
      {finalImage && <meta property="og:image" content={finalImage} />}
      {finalImage && <meta property="og:image:alt" content={finalTitle} />}
      {canonUrl && <meta property="og:url" content={canonUrl} />}
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content={g.site_name || "Places"} />
      {article?.published && <meta property="article:published_time" content={article.published} />}
      {article?.modified && <meta property="article:modified_time" content={article.modified} />}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}
      {(article?.tags || []).map((tg, i) => <meta key={`atag-${i}`} property="article:tag" content={tg} />)}

      {/* Twitter Card */}
      <meta name="twitter:card" content={g.twitter_card || "summary_large_image"} />
      {twitter && <meta name="twitter:site" content={twitter} />}
      {twitter && <meta name="twitter:creator" content={article?.authorTwitter || twitter} />}
      <meta name="twitter:title" content={finalTitle} />
      {finalDesc && <meta name="twitter:description" content={finalDesc} />}
      {finalImage && <meta name="twitter:image" content={finalImage} />}

      {/* Verification */}
      {g.google_site_verification && <meta name="google-site-verification" content={g.google_site_verification} />}
      {g.bing_site_verification && <meta name="msvalidate.01" content={g.bing_site_verification} />}
      {g.facebook_domain_verification && <meta name="facebook-domain-verification" content={g.facebook_domain_verification} />}
      {g.pinterest_site_verification && <meta name="p:domain_verify" content={g.pinterest_site_verification} />}
      {g.yandex_verification && <meta name="yandex-verification" content={g.yandex_verification} />}

      {/* Theme + PWA */}
      {branding.accent_orange && <meta name="theme-color" content={branding.accent_orange} />}
      {branding.favicon_url && <link rel="icon" href={branding.favicon_url} />}
      {branding.apple_touch_icon && <link rel="apple-touch-icon" href={branding.apple_touch_icon} />}

      {/* JSON-LD */}
      {lds.map((ld, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
}
